import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  TouchSensor,
  closestCorners,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/use-toast';
import { routeSettingBoardService } from '../../../services/route-setting-board';
import type {
  RouteSettingBoardCard,
  RouteSettingBoardType,
  RouteSettingZone,
} from '../../../types/route-setting-board';
import { useCurrentLocation } from '../../hooks/useCurrentLocation';
import { BoardCard } from './BoardCard';
import { BoardColumnView } from './BoardColumnView';
import { BoardFilterBar } from './BoardFilterBar';
import { BoardGroupingToggle } from './BoardGroupingToggle';
import { BoardSummaryBar } from './BoardSummaryBar';
import { ClimbImageDialog } from './ClimbImageDialog';
import {
  ageBounds,
  buildColumns,
  columnAssignment,
  columnIdOf,
  filterCards,
  gradesOf,
  holdColorsOf,
  settersOf,
  summarize,
  type BoardFilters,
  type BoardGrouping,
  type BoardSort,
} from './board-utils';
import {
  filtersFromParams,
  filtersToParams,
  groupingFromParams,
  groupingToParams,
  sortFromParams,
  sortToParams,
} from './filter-params';

interface RouteSettingBoardProps {
  /** Which wall this dashboard shows; the two pages differ only by this. */
  climbType: RouteSettingBoardType;
}

/**
 * The route-setting kanban. Columns are the gym's zones by default, ordered left
 * to right by descending average age, and cards move between them by drag and
 * drop. The same climbs can be cut by route setter or by grade instead — those
 * two are read-only, since a card has no zone to be dropped into there.
 *
 * Both dashboards (boulders and routes) render this same component; only
 * `climbType` differs.
 *
 * The filters live in the query string rather than in state, so a filtered
 * board is a link a setter can send to a colleague.
 */
export function RouteSettingBoard({ climbType }: RouteSettingBoardProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  // Not `useCurrentGym`: a stage of a trophy has the same kanban, and that
  // hook goes through the Gym-only endpoint, which 404s on one.
  const { slug, loading: gymLoading } = useCurrentLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [cards, setCards] = useState<RouteSettingBoardCard[]>([]);
  const [zones, setZones] = useState<RouteSettingZone[]>([]);
  const [loading, setLoading] = useState(true);
  // The failure's own message, or an empty string when it carried none. Kept
  // untranslated so `load` does not depend on `t` — a fresh `t` identity would
  // otherwise re-run the effect on every render.
  const [loadError, setLoadError] = useState<string | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  // The card itself is looked up on every render rather than held: the dialog
  // carries the same actions the card does, so it has to follow a confirm — and
  // close by itself when the climb it is showing is deleted.
  const [previewId, setPreviewId] = useState<string | null>(null);
  // Ages are derived from "now"; pinning it per load keeps every average and
  // every slider bound consistent instead of drifting between renders.
  const [now, setNow] = useState(() => Date.now());

  const sensors = useSensors(
    // A small threshold so a tap on a card is still a tap, not a drag.
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 6 } })
  );

  const load = useCallback(
    async (gymSlug: string) => {
      setLoading(true);
      setLoadError(null);
      try {
        const data = await routeSettingBoardService.list(gymSlug, climbType);
        setCards(data.climbs);
        setZones(data.zones);
        setNow(Date.now());
      } catch (e) {
        setLoadError(e instanceof Error ? e.message : '');
        setCards([]);
        setZones([]);
      } finally {
        setLoading(false);
      }
    },
    [climbType]
  );

  useEffect(() => {
    if (slug) load(slug);
  }, [slug, load]);

  const bounds = useMemo(() => ageBounds(cards, now), [cards, now]);
  const setters = useMemo(() => settersOf(cards), [cards]);
  const grades = useMemo(() => gradesOf(cards), [cards]);
  const holdColors = useMemo(() => holdColorsOf(cards), [cards]);

  // The URL is the source of truth, so a pasted link lands on the same board a
  // colleague was looking at.
  const filters = useMemo(
    () => filtersFromParams(searchParams, bounds),
    [searchParams, bounds]
  );
  const grouping = useMemo(() => groupingFromParams(searchParams), [searchParams]);
  const sort = useMemo(() => sortFromParams(searchParams), [searchParams]);

  const applyFilters = (next: BoardFilters) => {
    // `replace` so dragging the age slider does not fill up the back button.
    setSearchParams(filtersToParams(next, bounds, searchParams), { replace: true });
  };

  const applyGrouping = (next: BoardGrouping) =>
    setSearchParams(groupingToParams(next, searchParams), { replace: true });

  const applySort = (next: BoardSort) =>
    setSearchParams(sortToParams(next, searchParams), { replace: true });

  const resetFilters = () =>
    applyFilters({ name: '', ageRange: bounds, setterIds: [], grades: [], holdColors: [] });

  const visibleCards = useMemo(() => filterCards(cards, filters, now), [cards, filters, now]);
  const columns = useMemo(
    () => buildColumns(visibleCards, zones, grouping, now, sort),
    [visibleCards, zones, grouping, now, sort]
  );
  const summary = useMemo(() => summarize(visibleCards, now), [visibleCards, now]);

  const handleDragStart = (event: DragStartEvent) => setDraggingId(String(event.active.id));

  const handleDragEnd = async (event: DragEndEvent) => {
    setDraggingId(null);

    const { active, over } = event;
    if (!over || !slug) return;

    const climbId = String(active.id);
    const moved = cards.find((card) => card.id === climbId);
    const target = columns.find((column) => column.id === String(over.id));
    if (!moved || !target || columnIdOf(moved, grouping) === target.id) return;

    const { payload, patch, confirms } = columnAssignment(target);

    if (confirms) {
      const columnName = target.label ?? t(`gymAdmin.routeSettingBoard.unassigned.${grouping}`);
      const question = t(`gymAdmin.routeSettingBoard.confirmAssign.${grouping}`, {
        name: moved.name,
        target: columnName,
      });
      if (!window.confirm(question)) return;
    }

    // Optimistic: the card follows the pointer immediately, and only a failed
    // request puts it back. The response is the climb as saved, so the derived
    // fields (its grade's label and value) come back right even if the guess did
    // not cover them.
    setCards((current) =>
      current.map((card) => (card.id === climbId ? { ...card, ...patch } : card))
    );

    try {
      const saved = await routeSettingBoardService.assign(slug, climbId, payload);
      setCards((current) => current.map((card) => (card.id === climbId ? saved : card)));
    } catch (e) {
      setCards((current) => current.map((card) => (card.id === climbId ? moved : card)));
      toast({
        title: t('gymAdmin.routeSettingBoard.moveError'),
        description: e instanceof Error ? e.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    }
  };

  const handleConfirm = async (card: RouteSettingBoardCard) => {
    if (!slug) return;

    // Optimistic, like the move and the delete: the badge and the button go at
    // once, and only a failed request brings them back.
    setCards((current) =>
      current.map((entry) =>
        entry.id === card.id ? { ...entry, official_status: 'confirmed' } : entry
      )
    );

    try {
      const saved = await routeSettingBoardService.confirm(slug, card.id);
      setCards((current) => current.map((entry) => (entry.id === card.id ? saved : entry)));
      toast({ title: t('gymAdmin.routeSettingBoard.confirmed', { name: card.name }) });
    } catch (e) {
      setCards((current) => current.map((entry) => (entry.id === card.id ? card : entry)));
      toast({
        title: t('gymAdmin.routeSettingBoard.confirmError'),
        description: e instanceof Error ? e.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async (card: RouteSettingBoardCard) => {
    if (!slug) return;
    if (!window.confirm(t('gymAdmin.routeSettingBoard.confirmDelete', { name: card.name }))) return;

    // Optimistic, like the move: the card leaves the column at once and comes
    // back only if the request fails.
    setCards((current) => current.filter((entry) => entry.id !== card.id));

    try {
      await routeSettingBoardService.remove(slug, card.id);
      toast({ title: t('gymAdmin.routeSettingBoard.deleted', { name: card.name }) });
    } catch (e) {
      setCards((current) => [...current, card]);
      toast({
        title: t('gymAdmin.routeSettingBoard.deleteError'),
        description: e instanceof Error ? e.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    }
  };

  const draggingCard = draggingId ? cards.find((card) => card.id === draggingId) ?? null : null;
  const previewCard = previewId ? cards.find((card) => card.id === previewId) ?? null : null;

  if (gymLoading || loading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (loadError !== null) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-destructive">
          {loadError || t('errors.unexpectedError')}
        </CardContent>
      </Card>
    );
  }

  return (
    // As tall as the space the shell gives it: the toolbar and the column
    // headers hold their place while the columns scroll under them.
    <div className="flex h-full min-h-0 flex-col gap-3">
      {/* Grouping, filters and the headline numbers share one line: everything
          this row does not take is height the columns get. */}
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <div className="flex flex-wrap items-center gap-2">
          {cards.length > 0 && <BoardGroupingToggle value={grouping} onChange={applyGrouping} />}

          <BoardFilterBar
            filters={filters}
            onChange={applyFilters}
            sort={sort}
            onSortChange={applySort}
            bounds={bounds}
            setters={setters}
            grades={grades}
            holdColors={holdColors}
            onReset={resetFilters}
          />
        </div>

        <BoardSummaryBar summary={summary} />
      </div>

      {cards.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            {t(`gymAdmin.routeSettingBoard.empty.${climbType}`)}
          </CardContent>
        </Card>
      ) : /* Only the zone grouping needs zones; by setter or by grade the board
            still reads fine on a gym that has never drawn any. */
      grouping === 'zone' && zones.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            {t('gymAdmin.routeSettingBoard.noZones')}
          </CardContent>
        </Card>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={() => setDraggingId(null)}
        >
          {/* `min-h-[20rem]`: on a short window the board stops shrinking and
              the shell scrolls instead, rather than squeezing the columns down
              to a couple of cards. */}
          <div className="flex min-h-[20rem] flex-1 gap-4 overflow-x-auto pb-2">
            {columns.map((column) => (
              <BoardColumnView
                key={column.id}
                column={column}
                gymSlug={slug}
                averageAgeDays={summary.averageAgeDays}
                onCardImageClick={(card) => setPreviewId(card.id)}
                onCardConfirm={handleConfirm}
                onCardDelete={handleDelete}
              />
            ))}
          </div>

          <DragOverlay>
            {draggingCard ? (
              <div className="w-72">
                <BoardCard card={draggingCard} overlay />
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      <ClimbImageDialog
        card={previewCard}
        onOpenChange={(open) => !open && setPreviewId(null)}
        onConfirm={handleConfirm}
        onDelete={handleDelete}
      />
    </div>
  );
}
